import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Label, Popover, PopoverContent, PopoverTrigger, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { programContactHooks } from '../../Programs/relationshipHooks';
/**
 * Combobox over a program's own contacts, cloned from ProgramDocumentPicker's
 * shape: a plain controlled component rendered through EntityFormDialog's
 * `extraContent`. Value is the contact id; the label is its resolved name,
 * which ProgramContactProto already carries (no registry join). onChange also
 * returns the name so the caller can keep the legacy free-text field in sync
 * — the home strip prints that text, so an id-only save would blank it.
 *
 * Read-only over existing program contacts: create-new is deferred because a
 * contact write is the "programs" authz area while risk/issue writes are
 * "quality" (a quality-writer would get PermissionDenied).
 */ export function ContactPicker({ id, label, helpText, programId, value, onChange, onClear, testid, fallbackLabel, filter, hideLabel }) {
    const [open, setOpen] = useState(false);
    const { data, isLoading } = programContactHooks.useList({
        programId
    });
    const all = data?.items ?? [];
    const contacts = filter ? all.filter(filter) : all;
    const selected = contacts.find((c)=>c.contactId === value);
    const triggerLabel = selected?.contactName || fallbackLabel || 'Select a contact…';
    const handleSelect = (contactId, contactName)=>{
        onChange(contactId, contactName);
        setOpen(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: id,
                className: hideLabel ? 'sr-only' : undefined,
                children: label
            }),
            helpText && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: helpText
            }),
            /*#__PURE__*/ _jsxs(Popover, {
                open: open,
                onOpenChange: setOpen,
                children: [
                    /*#__PURE__*/ _jsx(PopoverTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            id: id,
                            type: "button",
                            variant: "secondary",
                            role: "combobox",
                            "aria-expanded": open,
                            className: "h-10 w-full justify-between bg-background font-normal",
                            "data-testid": testid ?? Testids.deliveryShared.fkCombobox,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate text-left",
                                    children: triggerLabel
                                }),
                                /*#__PURE__*/ _jsx(ChevronsUpDown, {
                                    className: "ml-2 h-4 w-4 shrink-0 opacity-50"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(PopoverContent, {
                        className: "w-[var(--radix-popover-trigger-width)] p-0",
                        align: "start",
                        children: /*#__PURE__*/ _jsxs(Command, {
                            label: `Search ${label}`,
                            children: [
                                /*#__PURE__*/ _jsx(CommandInput, {
                                    placeholder: "Search contacts by name…",
                                    "data-testid": Testids.deliveryShared.fkComboboxSearch
                                }),
                                /*#__PURE__*/ _jsxs(CommandList, {
                                    children: [
                                        isLoading && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs",
                                            children: "Loading…"
                                        }),
                                        !isLoading && contacts.length === 0 && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs text-muted-foreground",
                                            children: filter && all.length > 0 ? 'No matching contacts.' : 'No contacts on this program yet. Add them under Contacts.'
                                        }),
                                        (value || fallbackLabel) && onClear && /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: /*#__PURE__*/ _jsx(CommandItem, {
                                                value: "__clear__",
                                                keywords: [
                                                    'clear',
                                                    'none',
                                                    'remove'
                                                ],
                                                onSelect: ()=>{
                                                    onClear();
                                                    setOpen(false);
                                                },
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    children: "Clear selection"
                                                })
                                            })
                                        }),
                                        !isLoading && contacts.length > 0 && /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: contacts.map((c)=>/*#__PURE__*/ _jsx(CommandItem, {
                                                    value: c.contactId,
                                                    keywords: [
                                                        c.contactName
                                                    ],
                                                    "data-testid": Testids.deliveryShared.fkComboboxOption,
                                                    "data-row-id": c.contactId,
                                                    onSelect: ()=>handleSelect(c.contactId, c.contactName),
                                                    children: c.role ? `${c.contactName} — ${c.role}` : c.contactName
                                                }, c.contactId))
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
